<script setup lang="ts">
/**
 * 属性区色块拾色器：点击色块在旁弹出统一 RGB 拾色面板。
 */
import { nextTick, ref } from 'vue'
import AppColorPickerPanel from '@/components/AppColorPickerPanel.vue'
import { pickerColorToHex } from '@/utils/textColor'

const props = withDefaults(defineProps<{
  modelValue: string
  disabled?: boolean
  swatchWidth?: number
}>(), {
  disabled: false,
  swatchWidth: 42,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'panel-close': [value: string]
}>()

const showPanel = ref(false)
const openedHex = ref(pickerColorToHex(props.modelValue))
const draftHex = ref(pickerColorToHex(props.modelValue))
const anchorLeft = ref(0)
const anchorTop = ref(0)
const anchorHeight = ref(28)
const triggerRef = ref<HTMLButtonElement | null>(null)

function openPanelAtTrigger(): void {
  if (props.disabled) return
  const rect = triggerRef.value?.getBoundingClientRect()
  if (!rect) return
  openedHex.value = pickerColorToHex(props.modelValue)
  draftHex.value = openedHex.value
  anchorLeft.value = Math.round(rect.left)
  anchorTop.value = Math.round(rect.top)
  anchorHeight.value = Math.round(rect.height)
  showPanel.value = true
}

/**
 * 程序化打开拾色面板（兼容旧调用，默认使用当前色块位置）。
 */
function openPanel(): void {
  openPanelAtTrigger()
}

function handleLiveUpdate(hex: string): void {
  draftHex.value = hex
  emit('update:modelValue', hex)
}

function restoreFocusAfterPanelClose(): void {
  void nextTick(() => {
    triggerRef.value?.focus({ preventScroll: true })
  })
}

function handleConfirm(hex: string): void {
  showPanel.value = false
  emit('update:modelValue', hex)
  emit('panel-close', hex)
  restoreFocusAfterPanelClose()
}

function handleCancel(): void {
  showPanel.value = false
  draftHex.value = openedHex.value
  emit('update:modelValue', openedHex.value)
  restoreFocusAfterPanelClose()
}

defineExpose({
  openPanel,
})
</script>

<template>
  <div class="app-color-picker" :style="{ '--app-color-picker-size': `${swatchWidth}px` }">
    <button
      ref="triggerRef"
      type="button"
      class="app-color-picker__trigger"
      :disabled="disabled"
      @click="openPanelAtTrigger"
    >
      <span class="app-color-picker__fill" :style="{ background: modelValue }" />
    </button>
    <AppColorPickerPanel
      v-model:show="showPanel"
      :model-value="draftHex"
      :anchor-left="anchorLeft"
      :anchor-top="anchorTop"
      :anchor-height="anchorHeight"
      @update:model-value="handleLiveUpdate"
      @confirm="handleConfirm"
      @cancel="handleCancel"
    />
  </div>
</template>

<style scoped>
.app-color-picker {
  display: inline-flex;
  vertical-align: middle;
  width: var(--app-color-picker-size, 42px);
}

.app-color-picker__trigger {
  width: var(--app-color-picker-size, 42px);
  height: 28px;
  padding: 2px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  box-sizing: border-box;
  cursor: pointer;
}

.app-color-picker__trigger:disabled {
  cursor: not-allowed;
}

.app-color-picker__fill {
  display: block;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}
</style>
